import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Spesa, StatoCompletoSpesa } from 'src/app/common/models/Spesa';

@Injectable({
  providedIn: 'root'
})
export class SpeseDaSincronizzareService {
  private readonly STORAGE_KEY_SPESE_DA_SINCRONIZZARE_PREFISSO = '__speseDaSincronizzare';

  /**
   * Per ogni intervento, identificato dal proprio id, mantiene un Subject che propaga
   * ai sottoscriventi l'elenco degli id delle sue spese disallineate
   */
  private mapIdSpeseDisallineateSubject: Map<number, Subject<number[]>> = new Map<number, Subject<number[]>>();

  constructor(private storage: Storage) { }

  async getElencoIdSpeseDisallineatiObservable(idIntervento: number): Promise<Observable<number[]>> {
    const speseInterventoObs = this.getSpeseInterventoSubj(idIntervento);
    await this.aggiornaObservableIdSpeseDisallineate(idIntervento); // N spese ciascuna scatena la lettura dal Db
    return speseInterventoObs.asObservable();
  }

  private getSpeseInterventoSubj(idIntervento: number): Subject<number[]> {
    let subj = this.mapIdSpeseDisallineateSubject.get(idIntervento);
    if (!subj) { // Lo inizializzo a vuoto
      subj = new BehaviorSubject<number[]>([]);
      this.mapIdSpeseDisallineateSubject.set(idIntervento, subj);
    }
    return subj;
  }

  private getStorageKey(idIntervento: number): string {
    return `${this.STORAGE_KEY_SPESE_DA_SINCRONIZZARE_PREFISSO}_${idIntervento}`;
  }

  /**
   * Aggiunge al Db di tutte le spese dell'intervento 
   * l'oggetto passato.
   * Ritorna quanto ottenuto, dopo averlo salvato su Db
   * @param statoEventoDaRimuovere 
   * @returns tutte le spese da sincronizzare presenti per lo stesso intervento
   * cui apparteneva l'oggetto da aggiungere
   */
  async salvaSpesaDaSincronizzare(statoSpesaDaSalvare: StatoCompletoSpesa): Promise<StatoCompletoSpesa[]> {
    const speseFiltrate = await this.rimuoviSpesaDaSincronizzare(statoSpesaDaSalvare);
    speseFiltrate.unshift(statoSpesaDaSalvare);
    await this.storage.set(this.getStorageKey(statoSpesaDaSalvare.datiSpesa.idIntervento), speseFiltrate);
    return speseFiltrate;
  }

  /**
   * Rimuove dal Db tutte le spese dell'intervento aventi come id quello dell'oggetto
   * passato.
   * Ritorna quanto rimasto, dopo averlo salvato su Db
   * @param statoEventoDaRimuovere 
   * @returns tutte le spese da sincronizzare rimaste per lo stesso intervento
   * cui apparteneva l'oggetto da rimuovere
   */
  async rimuoviSpesaDaSincronizzare(statoSpesaDaRimuovere: StatoCompletoSpesa): Promise<StatoCompletoSpesa[]> {
    const { idIntervento, idProdotto } = statoSpesaDaRimuovere.datiSpesa;
    const statoPrecedenteSpese = await this.getSpeseDaSincronizzare(idIntervento);
    // Rimuovo l'eventuale stato precedente del singolo spesa
    const speseFiltrate = statoPrecedenteSpese.filter(statoSpesa => statoSpesa.datiSpesa.idProdotto !== idProdotto);
    await this.storage.set(this.getStorageKey(idIntervento), speseFiltrate);
    return speseFiltrate;
  }

  /**
   * Cancella TUTTE le spese disallineate associate all'intervento fornito
   * (nuove spese o modifiche non pubblicate) 
   * @param idIntervento 
   */
  async rimuoviSpeseDaSincronizzare(idIntervento: number) {
    const key = this.getStorageKey(idIntervento);
    await this.storage.remove(key);
  }

  /**
   * Ritorna true se per la spesa fornita esistono su Db
   * modifiche non sincronizzate (e quindi vanno mostrati
   * tali dati o un indicatore)
   * @param evento 
   */
  async isModificheDaSincronizzarePresenti(spesa: Spesa): Promise<boolean> {
    const { idIntervento, idProdotto } = spesa;
    return (!! await this.getSpesaDaSincronizzare(idIntervento, idProdotto));
  }

  async isSpesaPresenteSoloOffline(idIntervento: number, idProdotto: number): Promise<boolean> {
    const speseSoloOffline = await this.getSpesePresentiSoloOffline(idIntervento);
    return speseSoloOffline
      .map(spesa => spesa.datiSpesa.idProdotto)
      .includes(idProdotto);
  }

  async getSpesePresentiSoloOffline(idIntervento: number): Promise<StatoCompletoSpesa[]> {
    const speseDisallineati = await this.getSpeseDaSincronizzare(idIntervento);
    return speseDisallineati
      .filter(statoSpesa => statoSpesa.isPresenteSoloLocalmente === true);
  }

  /**
   * Ritorna la spesa da sincronizzare indicata. La spesa sottoscrivente dovrebbe sovrascrivere
   * i suoi dati con quelli ritornati perché le modifiche locali, per l'utente, prevalgono
   * @param idIntervento 
   * @param idEventoRicercato 
   */
  async getSpesaDaSincronizzare(idIntervento: number, idSpesaCercato: number): Promise<StatoCompletoSpesa> {
    const spese = await this.getSpeseDaSincronizzare(idIntervento);
    const spesaCompatibile = spese
      .filter(statoSpesa => statoSpesa.datiSpesa.idProdotto === idSpesaCercato)
    [0]; // Sempre uno
    return spesaCompatibile;
  }

  /**
   * Dopo un'operazione di aggiornamento ritrasmetto l'elenco
   * degli id di tutti le spese aperte ai sottoscriventi
   * @returns elenco degli id delle spese aperte attualmente presenti
   * su db
   */
  private async aggiornaObservableIdSpeseDisallineate(idIntervento: number): Promise<number[]> {
    const speseDaSincronizzare = await this.getSpeseDaSincronizzare(idIntervento);
    const elencoId = speseDaSincronizzare.map(statoSpesa => statoSpesa.datiSpesa.idProdotto);
    const speseSubj = this.getSpeseInterventoSubj(idIntervento);
    speseSubj.next(elencoId);
    return elencoId;
  }

  /**
   * Ritorna tutti le spese che risultano da sincronizzare per l'intervento
   * fornito o lista vuota
   * @param idIntervento 
   */
  async getSpeseDaSincronizzare(idIntervento: number): Promise<StatoCompletoSpesa[]> {
    const storageKey = this.getStorageKey(idIntervento);
    const speseSuStorage = await this.storage.get(storageKey);
    const speseDaSincronizzare = !!speseSuStorage ? speseSuStorage : [];
    return speseDaSincronizzare;
  }
}